import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getBaseUrl } from './lib/baseUrl';
import type { ApiVersion } from './lib/baseUrl';

export type { ApiVersion };

type ApiDocsContextValue = {
  apiKey: string;
  /** base url ตามเวอร์ชันที่เลือกอยู่ */
  baseUrl: string;
  /** base url ของ V2 (ใช้ในหน้า Authentication ที่โชว์ V2 ตายตัว) */
  baseUrlV2: string;
  apiVersion: ApiVersion;
  setApiVersion: (version: ApiVersion) => void;
  /** copy พร้อม toast + ทำ state ปุ่ม "Copied!" ให้ 2 วินาที */
  copyCode: (code: string, id?: string) => void;
  copiedStates: Record<string, boolean>;
};

const ApiDocsContext = createContext<ApiDocsContextValue | null>(null);

/** เรียกได้เฉพาะใน <ApiDocsProvider> */
export const useApiDocs = (): ApiDocsContextValue => {
  const ctx = useContext(ApiDocsContext);
  if (!ctx) throw new Error('useApiDocs must be used within <ApiDocsProvider>');
  return ctx;
};

type ProviderProps = {
  apiKey: string;
  apiVersion: ApiVersion;
  onApiVersionChange: (version: ApiVersion) => void;
  children: React.ReactNode;
};

export const ApiDocsProvider: React.FC<ProviderProps> = ({ apiKey, apiVersion, onApiVersionChange, children }) => {
  const { t } = useTranslation();
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  const copyCode = useCallback(async (code: string, id?: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          if (!successful) throw new Error('Copy command failed');
        } finally {
          document.body.removeChild(textArea);
        }
      }
      toast.success(t('apiDocs.codeCopied'));
      if (id) {
        setCopiedStates(prev => ({ ...prev, [id]: true }));
        setTimeout(() => setCopiedStates(prev => ({ ...prev, [id]: false })), 2000);
      }
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error(t('common.cannotCopy'));
    }
  }, [t]);

  const value = useMemo<ApiDocsContextValue>(() => ({
    apiKey,
    baseUrl: getBaseUrl(apiVersion),
    baseUrlV2: getBaseUrl('v2'),
    apiVersion,
    setApiVersion: onApiVersionChange,
    copyCode,
    copiedStates,
  }), [apiKey, apiVersion, onApiVersionChange, copyCode, copiedStates]);

  return <ApiDocsContext.Provider value={value}>{children}</ApiDocsContext.Provider>;
};
